/**
 * useOfflineSync — React hook for offline-first permit access
 * 
 * Provides:
 *   - permits: the best available data (online or cached)
 *   - isOffline: current connectivity state
 *   - pendingCount: number of queued offline mutations
 *   - queueAction: queue a mutation for offline-safe execution
 *   - lastSync: when data was last synced
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
    isOnline as checkOnline,
    onConnectivityChange,
    preCacheForUser,
    getCachedPermits,
    updateCachedPermit,
    addToSyncQueue,
    getSyncQueueCount,
    flushSyncQueue,
    getMeta
} from '../services/offlineService';
import { permitService } from '../services/permitService';

export default function useOfflineSync(userId) {
    const [offline, setOffline] = useState(!checkOnline());
    const [pendingCount, setPendingCount] = useState(0);
    const [lastSync, setLastSync] = useState(null);
    const [syncing, setSyncing] = useState(false);
    const flushingRef = useRef(false);

    // --- Connectivity Monitoring ---
    useEffect(() => {
        const cleanup = onConnectivityChange(
            // Online
            async () => {
                setOffline(false);
                await tryFlush();
            },
            // Offline
            () => setOffline(true)
        );
        return cleanup;
    }, []);

    // --- Flush queue on reconnect ---
    const tryFlush = useCallback(async () => {
        if (flushingRef.current) return;
        flushingRef.current = true;
        setSyncing(true);

        try {
            const result = await flushSyncQueue(permitService);
            if (result.success > 0) {
                console.log(`[useOfflineSync] Flushed ${result.success} items`);
            }
        } catch (err) {
            console.error('[useOfflineSync] Flush failed:', err);
        } finally {
            flushingRef.current = false;
            setSyncing(false);
            await refreshPendingCount();
        }
    }, []);

    // --- Pre-cache on mount (when online) ---
    useEffect(() => {
        if (!userId) return;

        const doPreCache = async () => {
            if (checkOnline()) {
                await preCacheForUser(userId, async (uid) => {
                    return permitService.getPermitsForUser(uid);
                });
            }
            await refreshPendingCount();
            const sync = await getMeta('lastSync');
            if (sync) setLastSync(sync);
        };

        doPreCache();

        // Refresh cache every 5 minutes while online
        const interval = setInterval(() => {
            if (checkOnline() && userId) {
                preCacheForUser(userId, async (uid) => {
                    return permitService.getPermitsForUser(uid);
                }).then(() => {
                    getMeta('lastSync').then(s => s && setLastSync(s));
                });
            }
        }, 5 * 60 * 1000);

        return () => clearInterval(interval);
    }, [userId]);

    // --- Queue an action for offline-safe execution ---
    const queueAction = useCallback(async (action, args, optimisticUpdate = null) => {
        if (checkOnline()) {
            // Online: execute immediately
            try {
                const method = permitService[action];
                if (typeof method === 'function') {
                    await method.apply(permitService, args);
                }
                return { queued: false, executed: true };
            } catch (err) {
                console.error(`[useOfflineSync] Online exec failed, queuing: ${action}`, err);
                // Fall through to queue
            }
        }

        // Offline or online-exec failed: queue it
        await addToSyncQueue(action, args);
        await refreshPendingCount();

        // Apply optimistic update to cached permit
        if (optimisticUpdate?.permitId && optimisticUpdate?.updates) {
            await updateCachedPermit(optimisticUpdate.permitId, optimisticUpdate.updates);
        }

        return { queued: true, executed: false };
    }, []);

    // --- Get permits (online-first, fallback to cache) ---
    const getPermits = useCallback(async () => {
        if (checkOnline()) {
            try {
                const permits = await permitService.getPermitsForUser(userId);
                // Also update cache
                preCacheForUser(userId, async () => permits);
                return { permits, fromCache: false };
            } catch (err) {
                console.warn('[useOfflineSync] Online fetch failed, using cache');
            }
        }

        // Fallback to cache
        const cached = await getCachedPermits();
        return { permits: cached, fromCache: true };
    }, [userId]);

    const refreshPendingCount = async () => {
        const count = await getSyncQueueCount();
        setPendingCount(count);
    };

    return {
        isOffline: offline,
        isSyncing: syncing,
        pendingCount,
        lastSync,
        queueAction,
        getPermits,
        tryFlush
    };
}
